import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Building2,
  CalendarRange,
  Check,
  FileJson,
  FileSpreadsheet,
  Loader2,
  Network,
  Braces,
  ChevronRight,
  Play,
  RefreshCw,
  Search,
  Table2,
  Target,
  Users,
} from "lucide-react";
import { useAppContext } from "../../shared/functions/Context";
import useBackButton from "../../shared/hooks/useBack";
import useTitle from "../../shared/hooks/useTitle";
import { logger } from "../../shared/functions/Logger";
import {
  exportDatabaseExcel,
  exportDatabaseJson,
  loadOrganizedScorecardRows,
  OrganizedScorecardRow,
} from "./lib/data-explorer/database-export";
import { queryOrganizedScorecards } from "./lib/data-explorer/organized-query";

type ExportFormat = "json" | "excel";

const QueryData = observer(() => {
  const { api, store, ui } = useAppContext();
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [loadingYears, setLoadingYears] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [rows, setRows] = useState<OrganizedScorecardRow[]>([]);
  const [query, setQuery] = useState("");
  const [queryDraft, setQueryDraft] = useState("select *");
  const [selectedDataset, setSelectedDataset] = useState("All scorecard data");
  const [resultView, setResultView] = useState<"table" | "json">("table");

  useTitle("Bulk Scorecard Export");
  useBackButton();

  useEffect(() => {
    let mounted = true;
    const loadYears = async () => {
      try {
        await api.scorecard.getAll();
      } catch (error) {
        logger.error("Unable to load financial years", error);
      } finally {
        if (mounted) setLoadingYears(false);
      }
    };
    loadYears();
    return () => { mounted = false; };
  }, [api.scorecard]);

  const scorecards = store.scorecard.all
    .map((item) => item.asJson)
    .sort((a, b) => String(b.description).localeCompare(String(a.description)));

  const exportScorecards = useCallback(async (format: ExportFormat) => {
    setExporting(format);
    try {
      if (format === "json") await exportDatabaseJson();
      else await exportDatabaseExcel();
      ui.snackbar.load({
        id: Date.now(),
        message: `All financial-year scorecards were exported as ${format === "json" ? "JSON" : "Excel"}.`,
        type: "success",
      });
    } catch (error) {
      logger.error("Bulk scorecard export failed", error);
      ui.snackbar.load({
        id: Date.now(),
        message: "The scorecard export could not be completed. Please try again.",
        type: "danger",
      });
    } finally {
      setExporting(null);
    }
  }, [ui.snackbar]);

  const loadQueryTable = useCallback(async () => {
    setLoadingTable(true);
    try {
      setRows(await loadOrganizedScorecardRows());
      ui.snackbar.load({ id: Date.now(), message: "Organized scorecard data is ready to query.", type: "success" });
    } catch (error) {
      logger.error("Unable to load organized scorecard table", error);
      ui.snackbar.load({ id: Date.now(), message: "The organized query table could not be loaded.", type: "danger" });
    } finally {
      setLoadingTable(false);
    }
  }, [ui.snackbar]);

  const queryResults = useMemo(() => queryOrganizedScorecards(rows, query), [rows, query]);
  const displayedRows = queryResults.slice(0, 250);
  const score = (value: number | null) => value === null ? "—" : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const datasets = [
    { label: "All scorecard data", query: "select *" },
    { label: "Financial years", query: 'select * where type="Company Scorecard"' },
    { label: "Company strategy", query: "select company" },
    { label: "Strategic goals", query: "select goals" },
    { label: "Sections", query: 'select * where type="Section"' },
    { label: "Departments", query: "select departments" },
    { label: "Users", query: "select users" },
    { label: "Objectives", query: "select objectives" },
    { label: "Measures", query: "select measures" },
  ];

  const openDataset = (label: string, datasetQuery: string) => {
    setSelectedDataset(label);
    setQueryDraft(datasetQuery);
    setQuery(datasetQuery);
  };

  return (
    <div className="scorecard-export uk-section uk-section-small">
      <div className="uk-container uk-container-xlarge">
        <section className="scorecard-export__hero">
          <div className="scorecard-export__hero-copy">
            <span className="scorecard-export__eyebrow">
              <CalendarRange size={15} /> Data export
            </span>
            <h1>Bulk scorecard export</h1>
            <p>
              Download every financial year in one organized file. The export joins the related
              records and calculates scorecard summaries, so it is ready to read and analyse.
            </p>
          </div>
          <div className="scorecard-export__year-count">
            <strong>{loadingYears ? "—" : scorecards.length}</strong>
            <span>financial years included</span>
          </div>
        </section>

        <div className="scorecard-export__layout">
          <main className="scorecard-export__main">
            <section className="scorecard-export__panel">
              <div className="scorecard-export__panel-heading">
                <div>
                  <span className="scorecard-export__step">1</span>
                  <h2>Financial years</h2>
                  <p>All available FY scorecards are included automatically.</p>
                </div>
                <span className="scorecard-export__included"><Check size={14} /> All selected</span>
              </div>

              <div className="scorecard-export__years">
                {loadingYears && <span className="scorecard-export__muted"><Loader2 size={15} className="spin" /> Loading financial years…</span>}
                {!loadingYears && scorecards.map((scorecard) => (
                  <span className="scorecard-export__year" key={scorecard.id}>
                    {scorecard.description}
                    {scorecard.active && <small>Active</small>}
                  </span>
                ))}
                {!loadingYears && !scorecards.length && (
                  <span className="scorecard-export__muted">No financial-year scorecards were found.</span>
                )}
              </div>
            </section>

            <section className="scorecard-export__panel">
              <div className="scorecard-export__panel-heading">
                <div>
                  <span className="scorecard-export__step">2</span>
                  <h2>Choose a file format</h2>
                  <p>Both formats contain the same joined and computed scorecard information.</p>
                </div>
              </div>

              <div className="scorecard-export__formats">
                <button
                  className="scorecard-export__format"
                  disabled={Boolean(exporting) || !scorecards.length}
                  onClick={() => exportScorecards("excel")}
                >
                  <span className="scorecard-export__format-icon scorecard-export__format-icon--excel">
                    {exporting === "excel" ? <Loader2 className="spin" /> : <FileSpreadsheet />}
                  </span>
                  <span>
                    <strong>{exporting === "excel" ? "Preparing workbook…" : "Export to Excel"}</strong>
                    <small>Six analysis-ready sheets for summaries, people, goals, objectives and measures.</small>
                  </span>
                  <span className="scorecard-export__extension">.XLSX</span>
                </button>

                <button
                  className="scorecard-export__format"
                  disabled={Boolean(exporting) || !scorecards.length}
                  onClick={() => exportScorecards("json")}
                >
                  <span className="scorecard-export__format-icon scorecard-export__format-icon--json">
                    {exporting === "json" ? <Loader2 className="spin" /> : <FileJson />}
                  </span>
                  <span>
                    <strong>{exporting === "json" ? "Preparing data…" : "Export to JSON"}</strong>
                    <small>A clean hierarchy grouped by financial year, department and employee UID.</small>
                  </span>
                  <span className="scorecard-export__extension">.JSON</span>
                </button>
              </div>
            </section>

            <section className="scorecard-export__panel scorecard-export__query-panel">
              <div className="scorecard-export__panel-heading">
                <div>
                  <span className="scorecard-export__step">3</span>
                  <h2>Query organized data</h2>
                  <p>A client-facing table of joined scorecard data — no database paths or collection structure.</p>
                </div>
                <button className="scorecard-export__load-button" onClick={loadQueryTable} disabled={loadingTable}>
                  {loadingTable ? <Loader2 size={15} className="spin" /> : rows.length ? <RefreshCw size={15} /> : <Search size={15} />}
                  {loadingTable ? "Building table…" : rows.length ? "Refresh data" : "Load query table"}
                </button>
              </div>

              {Boolean(rows.length) && (
                <div className="scorecard-export__data-workspace">
                  <nav className="scorecard-export__data-sidebar" aria-label="Organized datasets">
                    <div className="scorecard-export__data-project">
                      <span className="scorecard-export__project-mark">ECB</span>
                      <span><strong>Performance data</strong><small>Organized client model</small></span>
                    </div>
                    <span className="scorecard-export__sidebar-label">Datasets</span>
                    {datasets.map((dataset) => (
                      <button
                        key={dataset.label}
                        className={selectedDataset === dataset.label ? "is-active" : ""}
                        onClick={() => openDataset(dataset.label, dataset.query)}
                      >
                        <Table2 size={14} /><span>{dataset.label}</span><ChevronRight size={13} />
                      </button>
                    ))}
                    <div className="scorecard-export__privacy-note">
                      <Check size={13} /> Semantic view only
                      <small>Storage paths stay hidden</small>
                    </div>
                  </nav>

                  <div className="scorecard-export__data-console">
                    <div className="scorecard-export__console-tabbar">
                      <span className="is-active"><span className="scorecard-export__tab-dot" />{selectedDataset}</span>
                    </div>
                    <div className="scorecard-export__query-editor">
                      <div className="scorecard-export__editor-toolbar">
                        <span><Braces size={14} /> Query</span>
                        <small>Read-only organized dataset</small>
                      </div>
                      <div className="scorecard-export__editor-body">
                        <span className="scorecard-export__line-number">1</span>
                        <textarea
                          value={queryDraft}
                          onChange={(event) => setQueryDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if ((event.ctrlKey || event.metaKey) && event.key === "Enter") setQuery(queryDraft);
                          }}
                          spellCheck={false}
                          aria-label="Scorecard query editor"
                        />
                        <button onClick={() => setQuery(queryDraft)}><Play size={14} fill="currentColor" /> Run <kbd>Ctrl ↵</kbd></button>
                      </div>
                      <div className="scorecard-export__query-examples">
                        <span>Try:</span>
                        <button onClick={() => setQueryDraft('select user from uid "employee-id"')}>user by UID</button>
                        <button onClick={() => setQueryDraft('select measures where year="2025"')}>measures by year</button>
                        <button onClick={() => setQueryDraft('select * where department="Finance"')}>department</button>
                      </div>
                    </div>

                    <div className="scorecard-export__output-toolbar">
                      <div>
                        <button className={resultView === "table" ? "is-active" : ""} onClick={() => setResultView("table")}><Table2 size={14} /> Table</button>
                        <button className={resultView === "json" ? "is-active" : ""} onClick={() => setResultView("json")}><Braces size={14} /> JSON</button>
                      </div>
                      <span><strong>{queryResults.length.toLocaleString()}</strong> results{queryResults.length > displayedRows.length && ` · first ${displayedRows.length} shown`}</span>
                    </div>

                    {resultView === "table" && <div className="scorecard-export__table-wrap">
                      <table className="scorecard-export__table">
                      <thead><tr>
                        <th>Financial year</th><th>Record type</th><th>UID</th><th>Name</th>
                        <th>Department</th><th>Section</th><th>Strategic goal</th><th>Objective</th>
                        <th>Measure</th><th>Perspective</th><th>Review status</th>
                        <th>Midterm</th><th>Final</th><th>Target %</th><th>Complete %</th>
                      </tr></thead>
                      <tbody>
                        {displayedRows.map((row, index) => <tr key={`${row.financialYear}-${row.recordType}-${row.uid}-${index}`}>
                          <td>{row.financialYear}</td><td><span className="scorecard-export__type-tag">{row.recordType}</span></td>
                          <td className="scorecard-export__uid">{row.uid}</td><td>{row.name}</td><td>{row.department || "—"}</td>
                          <td>{row.section || "—"}</td><td>{row.strategicGoal || "—"}</td><td>{row.objective || "—"}</td>
                          <td>{row.measure || "—"}</td><td>{row.perspective || "—"}</td><td>{row.reviewStatus || "—"}</td>
                          <td>{score(row.midtermScore)}</td><td>{score(row.finalScore)}</td>
                          <td>{score(row.targetAchievement)}</td><td>{score(row.completion)}</td>
                        </tr>)}
                        {!displayedRows.length && <tr><td colSpan={15} className="scorecard-export__no-results">No rows match this query.</td></tr>}
                      </tbody>
                      </table>
                    </div>}
                    {resultView === "json" && (
                      <pre className="scorecard-export__json-view">{JSON.stringify(displayedRows, null, 2)}</pre>
                    )}
                  </div>
                </div>
              )}

              {!rows.length && !loadingTable && (
                <div className="scorecard-export__query-empty">
                  <Search size={24} />
                  <strong>Build the organized client table</strong>
                  <span>Data stays in this browser session and can be searched by UID, year, department, employee, goal, objective or measure.</span>
                </div>
              )}
            </section>
          </main>

          <aside className="scorecard-export__panel scorecard-export__contents">
            <h2>What is included</h2>
            <p>The export is assembled for reporting instead of copying the database structure.</p>
            <ul>
              <li><Target /><span><strong>Company strategy</strong><small>Strategic goals, objectives, measures and computed scores</small></span></li>
              <li><Network /><span><strong>Organization</strong><small>Business units, sections and department relationships</small></span></li>
              <li><Building2 /><span><strong>Departments</strong><small>Member UIDs, departmental scorecards and employee roll-ups</small></span></li>
              <li><Users /><span><strong>People by UID</strong><small>Employee details, review status, objectives and measures</small></span></li>
            </ul>
            <div className="scorecard-export__calculation-note">
              <strong>Computed automatically</strong>
              <span>Weighted midterm and final scores, target achievement, completion, objective counts and measure counts.</span>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
});

export default QueryData;
